import { faStarCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/UI/V3/Button/Button';
import { PriceFormat } from 'components/UI';
import { noop } from 'lodash';
import { PLAN_ENTITLEMENTS, PLAN_TIER_CONTENT } from 'model/Plans';
import { Subscription } from 'model/Subscriptions';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import styles from './CurrentPlanCard.module.scss';
import { PlanCard } from '../PlanCard';

interface Props {
    subscription: Subscription | undefined;
    onUpgrade: () => void;
    onDowngrade: (subscription: Subscription) => void;
}

export const CurrentPlanCard: React.FunctionComponent<Props> = ({ subscription, onUpgrade, onDowngrade }) => {
    const tier = subscription?.categoryId || 'starter';
    const content = PLAN_TIER_CONTENT[tier];

    const renewalDate = useMemo(
        () => subscription?.renewAt && moment(subscription.renewAt).format('MMM Do, YYYY'),
        [subscription?.renewAt]
    );

    const downgradeHandler = () => subscription && onDowngrade(subscription);

    return (
        <PlanCard className={styles.root}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div>
                        <span className={styles.label}>Current Plan</span>
                        <h1 className={styles.name}>{content.name}</h1>
                        <p className={styles.description}>{content.description}</p>
                    </div>

                    <div className={styles.actions}>
                        <Button
                            size="medium"
                            variant="outlined"
                            label="Cancel Plan"
                            onClick={noop}
                        />

                        {tier !== 'elite' && (
                            <Button
                                size="medium"
                                variant="primary"
                                label="Upgrade Plan"
                                onClick={onUpgrade}
                            />
                        )}

                        {tier !== 'starter' && (
                            <Button
                                size="medium"
                                variant="primary"
                                label="Downgrade Plan"
                                onClick={downgradeHandler}
                            />
                        )}
                    </div>
                </div>

                {subscription && (
                    <div className={styles.priceBlock}>
                        <div className={styles.price}>
                            <span className={styles.priceAmount}>
                                <PriceFormat price={`${subscription.price?.total}`} />
                            </span>
                            <span className={styles.priceCadence}>/mo</span>
                        </div>

                        {renewalDate && <p className={styles.renewal}>Renews {renewalDate}</p>}
                    </div>
                )}
            </div>

            <div className={styles.entitlements}>
                <p className={styles.entitlementsHeading}>
                    <FontAwesomeIcon icon={faStarCircle} />
                    {tier === 'elite' ? (
                        <span>
                            Enjoy every feature included in your <strong>Elite plan</strong>.
                        </span>
                    ) : (
                        <span>
                            Discover what you can do with a <strong>higher-tier plan</strong>.
                        </span>
                    )}
                </p>

                <div className={styles.entitlementsGrid}>
                    {PLAN_ENTITLEMENTS.map(entitlement => (
                        <div
                            key={entitlement.label}
                            className={styles.entitlementItem}
                        >
                            <FontAwesomeIcon icon={entitlement.icon} />
                            <span>{entitlement.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </PlanCard>
    );
};
